import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  useGridApiRef,
  gridFocusCellSelector,
  gridTabIndexCellSelector,
} from '@mui/x-data-grid';
import { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import OfficialAvatar from '@components/OfficialAvatar';
import { Toast } from '@components'
import { useUser } from '@hooks/useUser';
import { useAgency } from '@hooks/useAgency';
import { useInfo } from '@hooks/useInfo';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { downloadFile, fetchFileBlob, getReq } from '../../networkRequest/api';
import { fetchLocationSummaries } from '../../networkRequest/locationsService';
import { formatDateV2 } from '../../utils/formatDate';
import { Dict } from '../../utils/types';
import Utils from '../../utils/utils';
import ActivityHeaderControls from './components/ActivityHeaderControls';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, activityGridSx, utcFromDayjs } from './components/activityGrid';
import ClearancesToolbar from './components/ClearancesToolbar';
import { FilterState, FilterData, buildInitialFilterState, getFilterParams } from './components/filterState';
import ClearancesFilterBar from './components/ClearancesFilterBar';
import ExportPdfDialog from './components/ExportPdfDialog';
import ClearanceDetailSidebar from './components/ClearanceDetailSidebar';
import { Action, ClearanceFilterArgs, Clearance, ClearanceRow, QueryStringArgs, ReportQueryStringArgs } from './types';

const ACTION_FILTERS: Action[] = [
  { value: 'checked_out', name: 'Checked Out' },
  { value: 'automatic_check_out', name: 'Automatic Check Out' },
  { value: 'cleared_for_entry', name: 'Cleared For Entry' },
  { value: 'not_cleared_for_entry', name: 'Not Cleared For Entry' },
  { value: 'id_scan', name: 'OCID Scan' },
  { value: 'email_lookup', name: 'Email Lookup' },
  { value: 'qr_code_scan', name: 'QR Code Scan' },
];

const FIXED_COLUMNS = [
  { field: 'performed_at', headerName: 'Date & Time', width: 220 },
  {
    field: 'first_name',
    headerName: 'First',
    renderCell: (params) => (
      <OfficialAvatar
        img_url={params.row.img_url}
        name={params.row.first_name}
        square={false}
        officialTableView={false}
      />
    ),
    width: 150,
    sortable: false
  },
  { field: 'last_name', headerName: 'Last', width: 150, sortable: false },
  { field: 'agency', headerName: 'Agency', width: 150, sortable: false },
  { field: 'location_checkpoint', headerName: 'Location/Checkpoint', width: 200, sortable: false },
  { field: 'action', headerName: 'Action', width: 150, sortable: false },
  {
    field: 'verifier',
    headerName: 'Verifier',
    renderCell: (params) => (
      <OfficialAvatar
        img_url={params.row.verifier_img_url}
        name={params.row.verifier_name}
        square={false}
        officialTableView={false}
      />
    ),
    width: 200,
    sortable: false
  },
  { field: 'uncleared_reason', headerName: 'Not Cleared Reason', width: 200, sortable: false },
  { field: 'title', headerName: 'Title', width: 100, sortable: false },
  { field: 'rank', headerName: 'Rank', width: 100, sortable: false },
  { field: 'badge_number', headerName: 'Badge', width: 100, sortable: false },
  { field: 'identifier', headerName: 'Identifier', width: 200, sortable: false },
  { field: 'device_id', headerName: 'Device ID', width: 200, sortable: false },
  { field: 'official_capacity', headerName: 'Official Capacity', width: 150, sortable: false },
  { field: 'armed', headerName: 'Armed', width: 100, sortable: false },
  { field: 'armed_permission', headerName: 'Armed Permission', width: 150, sortable: false },
  { field: 'photo_matches', headerName: 'Photo Match', width: 150, sortable: false }
];

const appendFilterParams = (params: URLSearchParams, { searchText, startDt, endDt, checkpointIds, actions }: ClearanceFilterArgs) => {
  if (searchText) params.append('q', searchText);
  if (startDt) params.append('start', startDt);
  if (endDt) params.append('end', endDt);
  if (checkpointIds && checkpointIds.length > 0) params.append('checkpoint_id', checkpointIds.join(','));
  if (actions && actions.length > 0) params.append('action_name', actions.join(','));
};

const getQueryString = ({ page, pageSize, ...filterArgs }: QueryStringArgs) => {
  const params = new URLSearchParams();
  params.append('offset', (page * pageSize).toString());
  params.append('limit', pageSize.toString());
  appendFilterParams(params, filterArgs);
  return params.toString();
};

const getReportQueryString = ({ reportFormat, reportOffset, reportLimit, reportPageOffset, ...filterArgs }: ReportQueryStringArgs) => {
  const params = new URLSearchParams();
  if (reportOffset) params.append('offset', reportOffset.toString());
  if (reportLimit) params.append('limit', reportLimit.toString());
  if (reportPageOffset) params.append('report_page_offset', reportPageOffset.toString());
  appendFilterParams(params, filterArgs);
  params.append('report_format', reportFormat);
  params.append('report_tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
  return params.toString();
};

const actionTypeDisplayName = (action_type: string, check_in_action_type: string): string => {
  switch (check_in_action_type) {
    case 'check_out':
      return 'Checked Out';
    case 'check_out_new_location':
    case 'check_out_timeout':
    case 'check_out_uncleared':
      return 'Automatic Check Out';
  }
  switch (action_type) {
    case 'cleared':
      return 'Cleared For Entry';
    case 'uncleared':
      return 'Not Cleared For Entry';
    case 'id_scan':
      return 'OCID Scan';
    case 'email_lookup':
      return 'Email Lookup';
    case 'qr_code_scan':
      return 'QR Code Scan';
    default:
      return `${action_type} ${check_in_action_type}`;
  }
};

const getQuestionColumns = (data: Clearance[]): [Dict<string>, GridColDef<ClearanceRow>[]] => {
  const questionsSet = new Set(
    data
      .flatMap(clearance => clearance.additional_questions || [])
      .map(additionalQuestion => additionalQuestion.question)
  );
  const questions = Array.from(questionsSet).sort();

  const fieldNamesByQuestion = {};
  const questionColumns: GridColDef<ClearanceRow>[] = [];
  for (let i = 0; i < questions.length; i++) {
    const questionText = questions[i];
    const questionFieldName = `aq_${i}`;
    questionColumns.push({
      field: questionFieldName,
      headerName: questionText,
      width: 150,
      sortable: false,
      // Render a custom header to truncate the text from right to left
      // using CSS
      renderHeader: (params) => (
        <div
          className='MuiDataGrid-columnHeaderTitle'
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            direction: 'rtl'
          }}
          data-testid={`aq-header-${i}`}
        >
          {params.colDef.headerName}&lrm;
        </div>
      ),
      renderCell: (params) => (
        <span data-testid={`aq-${i}-${params.row.id}`}>
          {params.value}
        </span>
      )
    });
    fieldNamesByQuestion[questionText] = questionFieldName;
  }
  return [fieldNamesByQuestion, questionColumns];
};

// The Checkpoints tab of the Activity page
const CheckpointActivityLog: React.FC = () => {
  const { user } = useUser();
  const { agency } = useAgency();
  const { unclearedReasonDisplayName } = useInfo();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gridPagination, setGridPagination] = useState(() => ({
    page: Utils.parseIntParam(searchParams, 'page') ?? DEFAULT_PAGE,
    pageSize: Utils.parseIntParam(searchParams, 'page_size') ?? DEFAULT_PAGE_SIZE,
  }));
  const [pagination, setPagination] = useState({
    limit: 25,
    offset: 0,
    total_count: 0
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState(() => searchParams.get('search') ?? '');
  const [startDt, setStartDt] = useState<Dayjs | null>(() => Utils.parseDateTimeParam(searchParams, 'start'));
  const [endDt, setEndDt] = useState<Dayjs | null>(() => Utils.parseDateTimeParam(searchParams, 'end'));
  const [filterState, setFilterState] = useState<FilterState>(() => {
    const checkpointIds: Record<number, boolean> = Object.fromEntries(
      Utils.parseArrayParam(searchParams, 'checkpoint_ids')
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id))
        .map(id => [id, true])
    );
    const actions: Record<string, boolean> = Object.fromEntries(
      Utils.parseArrayParam(searchParams, 'actions')
        .map(action => [action, true])
    );
    return { checkpointIds, actions };
  });
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [clearancesData, setClearancesData] = useState<Clearance[]>([]);
  const [selectedClearanceId, setSelectedClearanceId] = useState<number | null>(null);
  const [filterDataError, setFilterDataError] = useState<boolean>(false);
  const [clearancesError, setClearancesError] = useState<boolean>(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState<boolean>(false);

  const fetchParams = useMemo(
    () => ({
      searchText,
      gridPagination,
      startDt: utcFromDayjs(startDt),
      endDt: utcFromDayjs(endDt),
      filterState
    }),
    [searchText, gridPagination, startDt, endDt, filterState]
  );
  const debouncedParams = useDebouncedValue(fetchParams, 300);
  // Built from the fetched clearances rather than in the fetch, so that the
  // uncleared reason display names loading from /info doesn't refetch them
  const [columns, rows] = useMemo((): [GridColDef<ClearanceRow>[], ClearanceRow[]] => {
    const [fieldNamesByQuestion, questionColumns] = getQuestionColumns(clearancesData);
    const clearanceRows = clearancesData.map((clearance) => {
      const additionalQuestions = clearance.additional_questions || [];
      const questionFields = Object.fromEntries(
        additionalQuestions.map(q => [
          fieldNamesByQuestion[q.question],
          Utils.formatBoolean(q.answer)
        ])
      );
      return {
        id: clearance.id,
        performed_at: formatDateV2(clearance.performed_at),
        img_url: clearance.agency_official?.img_url,
        first_name: clearance.agency_official?.first_name,
        last_name: clearance.agency_official?.last_name,
        agency: clearance.agency_official?.agency?.name,
        location_checkpoint: `${clearance.location?.name} / ${clearance.checkpoint?.name}`,
        action: actionTypeDisplayName(clearance.action_type, clearance.check_in_action_type),
        verifier_img_url: clearance.verifier?.img_url,
        verifier_name: `${clearance.verifier?.first_name} ${clearance.verifier?.last_name}`,
        uncleared_reason: unclearedReasonDisplayName(clearance.uncleared_reason, clearance.agency_official?.agency?.name),
        title: clearance.agency_official?.title,
        rank: clearance.agency_official?.rank,
        badge_number: clearance.agency_official?.badge_number,
        employment_type: clearance.agency_official?.employment_type,
        identifier: clearance.identifier,
        device_id: clearance.device_id,
        official_capacity: Utils.formatBoolean(clearance.official_capacity),
        armed: Utils.formatBoolean(clearance.armed),
        armed_permission: Utils.formatBoolean(clearance.armed_permission),
        photo_matches: Utils.formatBoolean(clearance.photo_matches),
        ...questionFields
      };
    });
    return [[...FIXED_COLUMNS, ...questionColumns], clearanceRows];
  }, [clearancesData, unclearedReasonDisplayName]);
  const selectedClearance = useMemo(
    () => clearancesData.find(c => c.id === selectedClearanceId) ?? null,
    [clearancesData, selectedClearanceId]
  );
  // Search count ref is used to ignore out-of-order responses
  const searchCountRef = useRef<number>(0);
  const apiRef = useGridApiRef();

  useEffect(
    () => {
      const fetchFilterData = async () => {
        if (!agency?.id || !user?.accessToken) return;
        try {
          setFilterDataError(false);
          const resp = await fetchLocationSummaries(user.accessToken, agency.id);
          if (resp.data === undefined) {
            throw new Error(resp.error);
          }
          const locations = resp.data;
          setFilterData({
            locations,
            actions: ACTION_FILTERS
          });
        }
        catch (error) {
          console.error('fetchFilterData error', error);
          setFilterDataError(true);
        }
      };
      fetchFilterData();
    },
    [agency?.id, user?.accessToken]
  );

  useEffect(
    () => {
      const fetchClearances = async () => {
        if (!agency?.id || !user?.accessToken) return;
        try {
          setClearancesError(false);
          setLoading(true);

          // Increment the search count and save it to the ref
          const newSearchCount = searchCountRef.current + 1;
          searchCountRef.current = newSearchCount;

          const queryString = getQueryString({
            page: debouncedParams.gridPagination.page,
            pageSize: debouncedParams.gridPagination.pageSize,
            searchText: debouncedParams.searchText,
            startDt: debouncedParams.startDt,
            endDt: debouncedParams.endDt,
            ...getFilterParams(debouncedParams.filterState)
          });
          const url = `/agencies/${agency?.id}/clearances?${queryString}`;

          const resp = await getReq<Clearance[]>(url, user?.accessToken);
          if (
            resp.data === undefined ||
            resp.pagination === undefined
          ) {
            throw new Error(resp.error);
          }
          // Ignore if response is older than the current search count
          if (newSearchCount < searchCountRef.current) {
            return;
          }

          setPagination(resp.pagination);

          setClearancesData(resp.data);
        }
        catch (error) {
          console.error('fetchClearances error', error);
          setClearancesError(true);
        }
        finally {
          setLoading(false);
        }
      };
      fetchClearances();
    },
    [agency?.id, user?.accessToken, debouncedParams]
  );

  // MUI keeps the grid body out of the tab order until a cell is focused, so a
  // keyboard user tabs from the column headers straight to the pagination,
  // skipping every row. Point the grid's tab target at the first data cell (its
  // tabIndex only — not focus, so we don't steal focus on mount). Tab then lands
  // inside the table body, and arrow keys navigate between cells and up to the
  // headers from there.
  useEffect(() => {
    if (!apiRef.current || rows.length === 0) return;
    // Don't fight an active focus or an existing tab target that's still on screen.
    if (gridFocusCellSelector(apiRef) !== null) return;
    const tabCell = gridTabIndexCellSelector(apiRef);
    if (tabCell !== null && rows.some((row) => row.id === tabCell.id)) return;

    const firstField = columns[0]?.field;
    if (!firstField) return;
    apiRef.current.setState((state) => ({
      ...state,
      tabIndex: { ...state.tabIndex, cell: { id: rows[0].id, field: firstField } },
    }));
  }, [apiRef, rows, columns]);

  // If the selected clearance becomes null because of a filter or page change,
  // set the selectedClearanceId to null to close the clearance detail side bar
  useEffect(
    () => {
      if (selectedClearance === null) {
        setSelectedClearanceId(null);
      }
    },
    [selectedClearance]
  );

  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedParams.gridPagination.page !== DEFAULT_PAGE) {
      params.page = String(debouncedParams.gridPagination.page);
    }
    if (debouncedParams.gridPagination.pageSize !== DEFAULT_PAGE_SIZE) {
      params.page_size = String(debouncedParams.gridPagination.pageSize);
    }
    if (debouncedParams.searchText) {
      params.search = debouncedParams.searchText;
    }
    if (debouncedParams.startDt) {
      params.start = debouncedParams.startDt;
    }
    if (debouncedParams.endDt) {
      params.end = debouncedParams.endDt;
    }
    const checkpointIds = Object.keys(debouncedParams.filterState.checkpointIds);
    if (checkpointIds.length > 0) {
      params.checkpoint_ids = checkpointIds.join(',');
    }
    const actions = Object.keys(debouncedParams.filterState.actions);
    if (actions.length > 0) {
      params.actions = actions.join(',');
    }
    setSearchParams(params, { replace: true });
  }, [debouncedParams, setSearchParams]);

  const fetchPdfReportChunk = (offset: number, limit: number, pageOffset: number) => {
    const queryString = getReportQueryString({
      reportFormat: 'pdf',
      reportOffset: offset,
      reportLimit: limit,
      reportPageOffset: pageOffset,
      searchText: debouncedParams.searchText,
      startDt: debouncedParams.startDt,
      endDt: debouncedParams.endDt,
      ...getFilterParams(debouncedParams.filterState),
    });
    const url = `/agencies/${agency?.id}/clearances_report?${queryString}`;
    return fetchFileBlob(url, user?.accessToken ?? '');
  };

  const exportReport = async (reportFormat) => {
    const accessToken = user?.accessToken;
    if (!accessToken) {
      return;
    }
    const queryString = getReportQueryString({
      reportFormat,
      searchText: debouncedParams.searchText,
      startDt: debouncedParams.startDt,
      endDt: debouncedParams.endDt,
      ...getFilterParams(debouncedParams.filterState),
    });
    const url = `/agencies/${agency?.id}/clearances_report?${queryString}`;

    await downloadFile(url, accessToken);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ActivityHeaderControls
        startDt={startDt}
        endDt={endDt}
        onStartDtChange={setStartDt}
        onEndDtChange={setEndDt}
        exportItems={[
          { label: 'Export CSV', onClick: () => { exportReport('csv'); } },
          { label: 'Export PDF', onClick: () => { setPdfDialogOpen(true); } }
        ]}
      />
      <ClearancesFilterBar
        filterData={filterData}
        filterState={filterState}
        onFilterChange={setFilterState}
        onClearAll={() => setFilterState(buildInitialFilterState())}
      />
      <DataGrid
        data-testid='activities-grid'
        apiRef={apiRef}
        rows={rows}
        rowCount={pagination.total_count}
        columns={columns}
        loading={loading}
        paginationMode='server'
        paginationModel={gridPagination}
        onPaginationModelChange={setGridPagination}
        pageSizeOptions={[10, 25, 50, 100]}
        getRowClassName={(params) => params.row.id === selectedClearanceId ? 'selected-clearance-row' : ''}
        onRowClick={(params) => setSelectedClearanceId(params.row.id as number)}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        sortingMode='server'
        filterMode='server'
        keepNonExistentRowsSelected={false}
        sx={activityGridSx}
        showToolbar
        slots={{ toolbar: ClearancesToolbar }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          },
          toolbar: {
            searchText,
            onSearchChange: (value) => {
              setSearchText(value);
              setGridPagination(prev => ({ ...prev, page: 0 }));
            }
          },
        }}
        localeText={{ toolbarColumns: 'Manage Columns' }}
      />
      <ClearanceDetailSidebar
        official={selectedClearance?.agency_official ?? null}
        onClose={() => setSelectedClearanceId(null)}
      />
      <ExportPdfDialog
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        totalCount={pagination.total_count}
        fetchPdfReportChunk={fetchPdfReportChunk}
      />
      {(filterDataError || clearancesError) && (
        <Toast
          type='error'
          onClose={() => {
            setFilterDataError(false);
            setClearancesError(false);
          }}
        >
          Something went wrong. Please check your connection and try again.
        </Toast>
      )}
    </Box>
  );
};

export default CheckpointActivityLog;
